import { useGetAllSlotData } from "api/stats";
import NumberHighlight from "./NumberHighlight";
import { DashboardGridItem, DoubleSpanGridContentWrapper } from "./styles";

import { Typography } from "components/typography";

import { IcbtRequest } from "api/types";
import { useGetPsychologistIcbtRequestsByDateRange } from "api/useIcbtQueries";
import { useGetAllProgramsAndTreatmentsQuery } from "../../hooks/icbt/useGetAllProgramsAndTreatmentsQuery";
import { Divider } from "@mindlercare/ui-web";

type TimeslotsDashboardItemProps = {
  psychologistUserId: number;
  fromDate: Date;
  toDate: Date;
  previousFromDate: Date;
  previousToDate: Date;
  dateRangeLengthInDays: number;
};

const IcbtDashboardGridItem = ({
  fromDate,
  toDate,
  previousFromDate,
  previousToDate,
  psychologistUserId: selectedPsychologistUserId,
  dateRangeLengthInDays,
}: TimeslotsDashboardItemProps): JSX.Element => {
  const { data: currentData } = useGetAllSlotData({
    fromDate: fromDate,
    toDate: toDate,
    psychologistUserId: selectedPsychologistUserId,
  });

  const { data: previousData } = useGetAllSlotData({
    fromDate: previousFromDate,
    toDate: previousToDate,
    psychologistUserId: selectedPsychologistUserId,
  });

  const { data: allProgramsAndTreatments } =
    useGetAllProgramsAndTreatmentsQuery(undefined);

  const { data: currentIcbtRequestData } =
    useGetPsychologistIcbtRequestsByDateRange({
      psychologistUserId: selectedPsychologistUserId,
      fromDate: fromDate,
      toDate: toDate,
    });

  const { data: previousIcbtRequestData } =
    useGetPsychologistIcbtRequestsByDateRange({
      psychologistUserId: selectedPsychologistUserId,
      fromDate: previousFromDate,
      toDate: previousToDate,
    });

  const slots = currentData?.slots;
  const previousSlots = previousData?.slots;

  const groupedIcbtRequests = currentIcbtRequestData?.reduce<{
    [key: string]: IcbtRequest[];
  }>((acc, request) => {
    const programId = request.programId;
    if (programId) {
      if (!acc[programId]) {
        acc[programId] = [];
      }
      acc[programId].push(request);
    }
    return acc;
  }, {});

  const groupedIcbtRequestsTuple: [string, IcbtRequest[]][] = Object.entries(
    groupedIcbtRequests || {},
  );

  const groupedRequestsWithTheProgramNameSorted = groupedIcbtRequestsTuple
    .map((tuple) => {
      const programId = tuple[0];
      const programs = allProgramsAndTreatments?.programs;
      const program = programs?.[programId];
      return {
        programTitle: program?.title,
        amountOfRequests: tuple[1].length,
      };
    })
    .sort((a, b) => b.amountOfRequests - a.amountOfRequests);

  const icbtsAssigned = currentIcbtRequestData?.length || 0;
  const meetingsCompleted = slots?.totalPastSlots as number;
  const blendingRatio = meetingsCompleted
    ? Math.round((icbtsAssigned / meetingsCompleted) * 100) / 100
    : undefined;

  const previousIcbtsAssigned = previousIcbtRequestData?.length || 0;
  const previousMeetingsCompleted = previousSlots?.totalPastSlots as number;
  const previousBlendingRatio = previousMeetingsCompleted
    ? Math.round((previousIcbtsAssigned / previousMeetingsCompleted) * 100) /
      100
    : undefined;

  const icbtAssignedDifference = icbtsAssigned - previousIcbtsAssigned;
  const blendingRatioDifference =
    blendingRatio && previousBlendingRatio
      ? blendingRatio - previousBlendingRatio
      : undefined;

  return (
    <DashboardGridItem doubleSpan>
      <DoubleSpanGridContentWrapper>
        <NumberHighlight
          title="Total iCBT assignment ratio"
          value={icbtsAssigned}
          difference={{
            changeAmount: icbtAssignedDifference,
            text: `compared to previous ${dateRangeLengthInDays} days`,
            coloring: icbtAssignedDifference > 0 ? "positive" : "negative",
          }}
        />
        {blendingRatio && (
          <>
            <Divider />
            <NumberHighlight
              title="iCBT per meeting (blending ratio)"
              value={blendingRatio}
              difference={
                blendingRatioDifference
                  ? {
                      changeAmount: blendingRatioDifference,
                      text: `compared to previous ${dateRangeLengthInDays} days`,
                      coloring:
                        blendingRatioDifference > 0 ? "positive" : "negative",
                    }
                  : undefined
              }
            />
          </>
        )}
      </DoubleSpanGridContentWrapper>

      <DoubleSpanGridContentWrapper>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <Typography variant="h3">Your top 5 most assigned programs</Typography>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* eslint-disable i18next/no-literal-string */}
          <Typography variant="body-x-small-bold" color="GRAY_BLACK">
            Name
          </Typography>
          <Typography variant="body-x-small-bold" color="GRAY_BLACK">
            #
          </Typography>
          {/* eslint-enable i18next/no-literal-string */}
        </div>
        {groupedRequestsWithTheProgramNameSorted.slice(0, 5).map((group) => {
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              key={group.programTitle}
            >
              <Typography variant="body" color="GRAY_DARK">
                {group.programTitle}
              </Typography>
              <Typography variant="body-bold">
                {group.amountOfRequests}
              </Typography>
            </div>
          );
        })}
      </DoubleSpanGridContentWrapper>
    </DashboardGridItem>
  );
};

export default IcbtDashboardGridItem;
